import { useState } from "react";
import OwnerBubble from "./OwnerBubble";
import {
  AddMasterFileCheckbox, useMasterFileConfigured, wantsMasterFile,
} from "../../components/masterFile";

// ============================================================================
// NEW CLIENT MODAL
// ============================================================================

export default function NewClientModal({ onClose, onSave, employees, statuses }) {
  const [formData, setFormData] = useState({
    name: "",
    group: "",
    owner: [],
    is_corporate: false,
    status: null,
    priority: null,
    year_end_date: "",
    due_date: "",
  });
  const masterFileConfigured = useMasterFileConfigured(formData.is_corporate);
  // null = untouched, i.e. follow the default (ticked when set up).
  const [addMaster, setAddMaster] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert("Client name is required");
      return;
    }
    
    // Clean up the form data before sending
    const cleanedData = {
      ...formData,
      // Remove empty strings for dates - backend expects null or valid date
      year_end_date: formData.year_end_date.trim() || undefined,
      due_date: formData.due_date.trim() || undefined,
      // Not a client field: the caller creates the client, THEN the master
      // file, so a SharePoint failure can never cost the new client.
      add_master_file: wantsMasterFile(masterFileConfigured, addMaster),
    };
    
    onSave(cleanedData);
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: "#ffffff",
          borderRadius: 12,
          padding: "24px 28px",
          width: 500,
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 20,
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: 20,
              fontWeight: 600,
              color: "#323338",
            }}
          >
            New Client
          </h3>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 4,
              display: "flex",
              color: "#676879",
            }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Name */}
            <div>
              <label
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#323338",
                  display: "block",
                  marginBottom: 6,
                }}
              >
                Client Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Enter client name"
                required
                autoFocus
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  fontSize: 14,
                  border: "1px solid #d0d4e4",
                  borderRadius: 8,
                  outline: "none",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#0073ea")}
                onBlur={(e) => (e.target.style.borderColor = "#d0d4e4")}
              />
            </div>

            {/* Type */}
            <div>
              <label
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#323338",
                  display: "block",
                  marginBottom: 6,
                }}
              >
                Type
              </label>
              <div style={{ display: "flex", gap: 12 }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="radio"
                    checked={!formData.is_corporate}
                    onChange={() =>
                      setFormData({ ...formData, is_corporate: false })
                    }
                  />
                  <span style={{ fontSize: 14 }}>Personal</span>
                </label>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="radio"
                    checked={formData.is_corporate}
                    onChange={() =>
                      setFormData({ ...formData, is_corporate: true })
                    }
                  />
                  <span style={{ fontSize: 14 }}>Corporate</span>
                </label>
              </div>
            </div>

            {/* Group */}
            <div>
              <label
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#323338",
                  display: "block",
                  marginBottom: 6,
                }}
              >
                Group
              </label>
              <input
                type="text"
                value={formData.group}
                onChange={(e) =>
                  setFormData({ ...formData, group: e.target.value })
                }
                placeholder="Optional"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  fontSize: 14,
                  border: "1px solid #d0d4e4",
                  borderRadius: 8,
                  outline: "none",
                }}
              />
            </div>

            {/* Owners */}
            <div>
              <label
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#323338",
                  display: "block",
                  marginBottom: 8,
                }}
              >
                Owners
              </label>
              <div
                style={{
                  maxHeight: 150,
                  overflowY: "auto",
                  border: "1px solid #d0d4e4",
                  borderRadius: 8,
                  padding: 8,
                }}
              >
                {employees
                  .filter((emp) => emp.is_active !== false)
                  .map((emp) => (
                    <label
                      key={emp.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "8px 10px",
                        cursor: "pointer",
                        borderRadius: 6,
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = "#f6f7fb")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = "transparent")
                      }
                    >
                      <input
                        type="checkbox"
                        checked={formData.owner.includes(emp.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              owner: [...formData.owner, emp.id],
                            });
                          } else {
                            setFormData({
                              ...formData,
                              owner: formData.owner.filter(
                                (id) => id !== emp.id
                              ),
                            });
                          }
                        }}
                      />
                      <OwnerBubble owner={emp.name} size={24} />
                      <span style={{ fontSize: 14 }}>{emp.name}</span>
                    </label>
                  ))}
              </div>
            </div>

            <div>
              <label>Fiscal Year</label>
              <input
                type="number"
                min="2000"
                max="2100"
                value={formData.fiscal_year || new Date().getFullYear()}
                onChange={(e) =>
                  setFormData({ ...formData, fiscal_year: e.target.value })
                }
                placeholder="YYYY"
              />
            </div>

            <div>
              <label>Client Status</label>
              <select
                value={formData.client_status || "active"}
                onChange={(e) =>
                  setFormData({ ...formData, client_status: e.target.value })
                }
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            {/* Year End Date (optional for corporate) */}
            {formData.is_corporate && (
              <div>
                <label
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#323338",
                    display: "block",
                    marginBottom: 6,
                  }}
                >
                  Year End Date
                </label>
                <input
                  type="date"
                  value={formData.year_end_date}
                  onChange={(e) =>
                    setFormData({ ...formData, year_end_date: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    fontSize: 14,
                    border: "1px solid #d0d4e4",
                    borderRadius: 8,
                    outline: "none",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#0073ea")}
                  onBlur={(e) => (e.target.style.borderColor = "#d0d4e4")}
                />
              </div>
            )}

            {/* Due Date (optional for corporate) */}
            {formData.is_corporate && (
              <div>
                <label
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#323338",
                    display: "block",
                    marginBottom: 6,
                  }}
                >
                  Due Date
                </label>
                <input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) =>
                    setFormData({ ...formData, due_date: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    fontSize: 14,
                    border: "1px solid #d0d4e4",
                    borderRadius: 8,
                    outline: "none",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#0073ea")}
                  onBlur={(e) => (e.target.style.borderColor = "#d0d4e4")}
                />
              </div>
            )}

            <AddMasterFileCheckbox
              configured={masterFileConfigured}
              isCorporate={formData.is_corporate}
              checked={addMaster}
              onChange={setAddMaster}
            />
          </div>

          <div
            style={{
              display: "flex",
              gap: 10,
              justifyContent: "flex-end",
              marginTop: 24,
            }}
          >
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: "10px 20px",
                fontSize: 14,
                border: "1px solid #d0d4e4",
                borderRadius: 8,
                backgroundColor: "#ffffff",
                color: "#323338",
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: "10px 20px",
                fontSize: 14,
                border: "none",
                borderRadius: 8,
                backgroundColor: "#0073ea",
                color: "#ffffff",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Create Client
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
